/**
 * Masked Field Type
 *
 * Provides custom column renderer for masked/sensitive data display.
 * This type is read-only only (masked data is not editable inline).
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Text } from 'components';
import { MaskedCell } from '../../styles';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../types';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

// Import shared cell value utility
import { getCellStringValue } from './cellValue';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Default mask function for sensitive data (like API keys)
 * Shows last 4 characters with dots prefix
 */
export const defaultMaskFn = (value: string): string => {
  if (!value || value.length < 4) return value;
  const lastFour = value.slice(-4);
  return `••••• ${lastFour}`;
};

/**
 * Masked read-only renderer - renders sensitive data with masking
 * Uses maskFn to transform the displayed value
 */
export const maskedReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const colWithMask = col as CustomColumn<T> & {
    maskFn?: (value: string) => string;
    style?: React.CSSProperties;
  };
  const maskFn = colWithMask.maskFn ?? defaultMaskFn;

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use shared getCellStringValue to respect accessorFn (important for custom fields)
      const value = getCellStringValue(context, col.id);
      return (
        <MaskedCell>
          <Text variant="bodyXs" style={colWithMask.style}>
            {maskFn(value)}
          </Text>
        </MaskedCell>
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      // Use shared getCellStringValue to respect accessorFn
      const value = getCellStringValue(cell, col.id);
      return maskFn(value);
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const maskedReadOnly: ColumnRenderer = maskedReadOnlyRenderer;

/** Editable renderer - null because masked columns are not editable */
export const maskedEditable: ColumnRenderer | null = null;
